import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../authContext';
import { createTemplate, updateTemplate, getTemplate } from '../../../api/login.service';
import { uploadMediaForWhatsApp } from '../../../api/media.service';

// Reuse your existing field components when available in the project
import InputField from 'components/fields/InputField';
import SelectField from 'components/fields/SelectField';
import DialogDefault from '../event_create_edit/components/steps/DialogDefault';
import { RiDeleteBinLine } from 'react-icons/ri';

// --- Types
type HeaderType = 'none' | 'text' | 'image' | 'video' | 'document';

interface VariableSample {
  index: number;
  sample: string;
}

interface TemplateButton {
  kind: 'quick_reply' | 'url' | 'phone' | 'otp' | 'flow';
  text: string;
  url?: string;
  url_type?: 'static' | 'dynamic';
  example_url?: string;
  phone?: string;
  otp_type?: 'ZERO_TAP' | 'ONE_TAP';
  flow_id?: string;
  navigate_screen?: string;
  flow_action?: string;
}

// --- Helpers

const uid = () => Math.random().toString(36).slice(2, 9);

const LANGUAGES = [
  { value: 'af', label: 'Afrikaans' },
  { value: 'sq', label: 'Albanian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ar_EG', label: 'Arabic (EGY)' },
  { value: 'ar_AE', label: 'Arabic (UAE)' },
  { value: 'ar_LB', label: 'Arabic (LBN)' },
  { value: 'ar_MA', label: 'Arabic (MAR)' },
  { value: 'ar_QA', label: 'Arabic (QAT)' },
  { value: 'az', label: 'Azerbaijani' },
  { value: 'be_BY', label: 'Belarusian' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bn_IN', label: 'Bengali (IND)' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'ca', label: 'Catalan' },
  { value: 'zh_CN', label: 'Chinese (CHN)' },
  { value: 'zh_HK', label: 'Chinese (HKG)' },
  { value: 'zh_TW', label: 'Chinese (TAI)' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'prs_AF', label: 'Dari' },
  { value: 'nl', label: 'Dutch' },
  { value: 'nl_BE', label: 'Dutch (BEL)' },
  { value: 'en', label: 'English' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_AE', label: 'English (UAE)' },
  { value: 'en_AU', label: 'English (AUS)' },
  { value: 'en_CA', label: 'English (CAN)' },
  { value: 'en_GH', label: 'English (GHA)' },
  { value: 'en_IE', label: 'English (IRL)' },
  { value: 'en_IN', label: 'English (IND)' },
  { value: 'en_JM', label: 'English (JAM)' },
  { value: 'en_MY', label: 'English (MYS)' },
  { value: 'en_NZ', label: 'English (NZL)' },
  { value: 'en_QA', label: 'English (QAT)' },
  { value: 'en_SG', label: 'English (SGP)' },
  { value: 'en_UG', label: 'English (UGA)' },
  { value: 'en_ZA', label: 'English (ZAF)' },
  { value: 'et', label: 'Estonian' },
  { value: 'fil', label: 'Filipino' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'fr_BE', label: 'French (BEL)' },
  { value: 'fr_CA', label: 'French (CAN)' },
  { value: 'fr_CH', label: 'French (CHE)' },
  { value: 'fr_CI', label: 'French (CIV)' },
  { value: 'fr_MA', label: 'French (MAR)' },
  { value: 'ka', label: 'Georgian' },
  { value: 'de', label: 'German' },
  { value: 'de_AT', label: 'German (AUT)' },
  { value: 'de_CH', label: 'German (CHE)' },
  { value: 'el', label: 'Greek' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ha', label: 'Hausa' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ga', label: 'Irish' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'kk', label: 'Kazakh' },
  { value: 'rw_RW', label: 'Kinyarwanda' },
  { value: 'ko', label: 'Korean' },
  { value: 'ky_KG', label: 'Kyrgyz (Kyrgyzstan)' },
  { value: 'lo', label: 'Lao' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mr', label: 'Marathi' },
  { value: 'nb', label: 'Norwegian' },
  { value: 'ps_AF', label: 'Pashto' },
  { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt_BR', label: 'Portuguese (BR)' },
  { value: 'pt_PT', label: 'Portuguese (POR)' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ro', label: 'Romanian' },
  { value: 'ru', label: 'Russian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'si_LK', label: 'Sinhala' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'es', label: 'Spanish' },
  { value: 'es_AR', label: 'Spanish (ARG)' },
  { value: 'es_CL', label: 'Spanish (CHL)' },
  { value: 'es_CO', label: 'Spanish (COL)' },
  { value: 'es_CR', label: 'Spanish (CRI)' },
  { value: 'es_DO', label: 'Spanish (DOM)' },
  { value: 'es_EC', label: 'Spanish (ECU)' },
  { value: 'es_HN', label: 'Spanish (HND)' },
  { value: 'es_MX', label: 'Spanish (MEX)' },
  { value: 'es_PA', label: 'Spanish (PAN)' },
  { value: 'es_PE', label: 'Spanish (PER)' },
  { value: 'es_ES', label: 'Spanish (SPA)' },
  { value: 'es_UY', label: 'Spanish (URY)' },
  { value: 'sw', label: 'Swahili' },
  { value: 'sv', label: 'Swedish' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'uz', label: 'Uzbek' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'zu', label: 'Zulu' },
];

const CATEGORIES = [
  { value: 'UTILITY', label: 'Utility' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Authentication / OTP' },
];

const HEADER_TYPES: { value: HeaderType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'document', label: 'Document' },
];

const MAX_BODY = 1024; // WhatsApp limit for body text
const MAX_HEADER_TEXT = 60; // WA limit for header text
const MAX_FOOTER = 60; // WA limit for footer text
const MAX_QUICK_REPLIES = 3;
const MAX_BUTTONS = 10; // WA max (list after 3)

// Media file size limits (in bytes)
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_SIZE = 16 * 1024 * 1024; // 16MB
const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10MB

// Supported file types
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/3gpp'];
const SUPPORTED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
];

// Convert Quill HTML to plaintext WA body
function quillHtmlToText(html: string) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>(?=\n?)/gi, '\n') // br -> \n
    .replace(/<p[^>]*>/gi, '') // remove opening p
    .replace(/<\/p>/gi, '\n') // close p -> \n
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, '') // strip other tags
    .replace(/\n{3,}/g, '\n\n') // collapse extra newlines
    .trim();
}

export default function WhatsappTemplateCreate() {
  const { templateId } = useParams<{ templateId?: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Get wabaId from session storage instead of URL params
  const wabaId = sessionStorage.getItem('wabaId');
  
  console.log('WhatsappTemplateCreate: Component loaded');
  console.log('WhatsappTemplateCreate: isAuthenticated:', isAuthenticated);
  console.log('WhatsappTemplateCreate: templateId:', templateId);
  console.log('WhatsappTemplateCreate: wabaId from session storage:', wabaId);
  
  // Check if we're in edit mode
  const isEditMode = !!templateId;
  
  // meta
  const [name, setName] = useState('user_registration_notice_v1');
  const [language, setLanguage] = useState('en_US');
  const [category, setCategory] = useState('UTILITY');

  // header
  const [headerType, setHeaderType] = useState<HeaderType>('image');
  const [headerText, setHeaderText] = useState('Acme Account Created');
  const [headerUpload, setHeaderUpload] = useState<File | null>(null);
  const [headerMediaId, setHeaderMediaId] = useState<string | null>("");
  const [headerMediaUrl, setHeaderMediaUrl] = useState<string | null>(null);
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState<string | null>(null); // For image preview
  const [uploadingHeader, setUploadingHeader] = useState(false);

  // body + variables
  const [body, setBody] = useState(
    "Hi there! This is your new WhatsApp template message."
  );
  const [variableSamples, setVariableSamples] = useState<VariableSample[]>([]);

  // footer
  const [footer, setFooter] = useState('To opt out , type STOP');

  // buttons
  const [buttons, setButtons] = useState<TemplateButton[]>([]);

  // validity period
  const [customValidity, setCustomValidity] = useState(false);
  const [validityMinutes, setValidityMinutes] = useState<number>(10);
  
  // submit dialog
  const [openConfirm, setOpenConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Loading state for edit mode
  const [loading, setLoading] = useState(isEditMode);

  // Fetch existing template data if in edit mode
  useEffect(() => {
    if (isEditMode && templateId) {
      fetchTemplateData(templateId);
    }
  }, [isEditMode, templateId]);

  const fetchTemplateData = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const templateData = await getTemplate(id);
      
      // Populate form with existing data
      setName(templateData.name || '');
      setLanguage(templateData.language || 'en_US');
      setCategory(templateData.category || 'UTILITY');
      
      // Handle components
      if (templateData.components) {
        templateData.components.forEach((component: any) => {
          if (component.type === 'HEADER') {
            if (component.format === 'TEXT') {
              setHeaderType('text');
              setHeaderText(component.text || '');
            } else if (component.format === 'IMAGE') {
              setHeaderType('image');
              const existingHandle = component.example?.header_handle?.[0] || null;
              console.log('Loading existing template - header_handle:', existingHandle);
              // For existing templates, we need to re-upload to get file_handle
              // Clear the media URL to force re-upload
              setHeaderMediaUrl(null);
              setHeaderMediaId(null);
            }
          } else if (component.type === 'BODY') {
            setBody(component.text || '');
            // Extract variables from body text
            const variableMatches = component.text.match(/\{\{(\d+)\}\}/g) || [];
            const variables = variableMatches.map((match: string) => {
              const index = parseInt(match.replace(/\{\{|\}\}/g, ''));
              return { index, sample: `Variable ${index}` };
            });
            setVariableSamples(variables);
          } else if (component.type === 'FOOTER') {
            setFooter(component.text || '');
          } else if (component.type === 'BUTTONS' && component.buttons) {
            const loadedButtons: TemplateButton[] = component.buttons.map((btn: any) => {
              if (btn.type === 'QUICK_REPLY') {
                return { kind: 'quick_reply', text: btn.text };
              } else if (btn.type === 'URL') {
                const isDynamic = btn.example && btn.example.length > 0;
                return {
                  kind: 'url',
                  text: btn.text,
                  url: btn.url,
                  url_type: isDynamic ? 'dynamic' : 'static',
                  example_url: isDynamic ? btn.example[0] : undefined
                };
              } else if (btn.type === 'PHONE_NUMBER') {
                return { kind: 'phone', text: btn.text, phone: btn.phone_number };
              } else if (btn.type === 'OTP') {
                return { kind: 'otp', otp_type: btn.otp_type || 'ONE_TAP' };
              } else if (btn.type === 'FLOW') {
                return {
                  kind: 'flow',
                  text: btn.text,
                  flow_id: btn.flow_id,
                  navigate_screen: btn.navigate_screen,
                  flow_action: btn.flow_action || 'navigate'
                };
              }
              return null;
            }).filter(Boolean) as TemplateButton[];
            setButtons(loadedButtons);
          }
        });
      }
      
    } catch (error) {
      console.error('Error fetching template data:', error);
      setError('Failed to load template data');
    } finally {
      setLoading(false);
    }
  };

  const bodyVariables = useMemo(() => {
    const matches = body.match(/\{\{(\d+)\}\}/g) || [];
    return matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''))).sort((a, b) => a - b);
  }, [body]);

  // Ensure sample inputs exist for all variables used in body
  useEffect(() => {
    setVariableSamples((prev) => {
      const map = new Map(prev.map(v => [v.index, v.sample]));
      return bodyVariables.map((i) => ({ index: i, sample: map.get(i) ?? '' }));
    });
  }, [bodyVariables]);

  const removeVariable = (idx: number) => {
    setBody(prev => prev.replace(new RegExp(`\\{\\{${idx}\\}\\}`, 'g'), ''));
  };

  const updateSample = (idx: number, value: string) => {
    setVariableSamples((prev) => prev.map((v) => (v.index === idx ? { ...v, sample: value } : v)));
  };

  const quillRef = useRef<ReactQuill>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddVariableAtCursor = (ref: ReactQuill | null) => {
    if (!ref) return;
    const editor = ref.getEditor();
    const range = editor.getSelection();
    const nextIndex = (bodyVariables[bodyVariables.length - 1] || 0) + 1;
    const variableText = `{{${nextIndex}}}`;
    
    if (range) {
      editor.insertText(range.index, variableText);
    } else {
      editor.insertText(editor.getLength(), variableText);
    }
    
    // Update samples
    variableSamples.forEach((v) => {
      if (v.index === nextIndex) return;
    });
    setVariableSamples(prev => [...prev, { index: nextIndex, sample: '' }]);
  };

  const compiledPreview = useMemo(() => {
    let preview = body;
    variableSamples.forEach((v) => {
      preview = preview.replace(new RegExp(`\\{\\{${v.index}\\}\\}`, 'g'), v.sample || `{{${v.index}}}`);
    });
    return preview;
  }, [body, variableSamples]);

  const handleHeaderMediaUpload = async (file: File) => {
    setUploadingHeader(true);
    try {
      // Create a preview URL from the file for display
      const previewUrl = URL.createObjectURL(file);
      
      const data = await uploadMediaForWhatsApp(file);
      console.log('Media upload response:', data);
      console.log('data.file_handle:', data.file_handle);
      console.log('data.mediaId:', data.mediaId);
      
      const fileHandle = data.file_handle || data.mediaId || '';
      console.log('Setting headerMediaId to:', fileHandle);
      
      if (!fileHandle) {
        throw new Error('No file handle returned from upload');
      }
      
      setHeaderMediaId(fileHandle);
      setHeaderMediaUrl(fileHandle); // Store for API
      setHeaderPreviewUrl(previewUrl); // Store for preview display
      setSuccess('Header media uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload header media');
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleHeaderMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleHeaderMediaChange', e.target.files);
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type and size
    const isValidType = SUPPORTED_IMAGE_TYPES.includes(file.type) || 
                       SUPPORTED_VIDEO_TYPES.includes(file.type) || 
                       SUPPORTED_DOCUMENT_TYPES.includes(file.type);
    
    if (!isValidType) {
      setError('Unsupported file type');
      return;
    }
    
    const maxSize = headerType === 'image' ? MAX_IMAGE_SIZE : 
                   headerType === 'video' ? MAX_VIDEO_SIZE : MAX_DOCUMENT_SIZE;
    
    if (file.size > maxSize) {
      setError(`File too large. Max size: ${maxSize / (1024 * 1024)}MB`);
      return;
    }
    
    setHeaderUpload(file);
    handleHeaderMediaUpload(file);
  };

  const removeHeaderMedia = () => {
    setHeaderUpload(null);
    setHeaderMediaId(null);
    setHeaderMediaUrl(null);
    setHeaderPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addButton = (kind: TemplateButton['kind']) => {
    if (buttons.length >= MAX_BUTTONS) {
      setError(`Maximum ${MAX_BUTTONS} buttons allowed`);
      return;
    }
    
    const newButton: TemplateButton = {
      kind,
      text: '',
      ...(kind === 'url' ? { url: '', url_type: 'static' } : {}),
      ...(kind === 'phone' ? { phone: '' } : {}),
      ...(kind === 'otp' ? { otp_type: 'ONE_TAP' } : {}),
      ...(kind === 'flow' ? { flow_id: '', navigate_screen: '', flow_action: 'navigate' } : {}),
    };
    
    setButtons(prev => [...prev, newButton]);
  };

  const updateButton = (index: number, updates: Partial<TemplateButton>) => {
    setButtons(prev => prev.map((btn, i) => i === index ? { ...btn, ...updates } : btn));
  };

  const removeButton = (index: number) => {
    setButtons(prev => prev.filter((_, i) => i !== index));
  };

  function buildComponents() {
    console.log('Building components', headerType);
    const comps: any[] = [];
    
    // Header
    if (headerType !== 'none') {
      if (headerType === 'text') {
        comps.push({
          type: 'HEADER',
          format: 'TEXT',
          text: headerText,
        });
      } else if (headerType === 'image' && headerMediaId) {
        console.log('Building IMAGE header component - headerMediaUrl:', headerMediaUrl);
        comps.push({
          type: 'HEADER',
          format: 'IMAGE',
          example: {
            header_handle: [headerMediaUrl]
          }
        });
      } else if (headerType === 'video' && headerMediaId) {
        comps.push({
          type: 'HEADER',
          format: 'VIDEO',
          example: {
            header_handle: [headerMediaUrl]
          }
        });
      } else if (headerType === 'document' && headerMediaId) {
        comps.push({
          type: 'HEADER',
          format: 'DOCUMENT',
          example: {
            header_handle: [headerMediaUrl]
          }
        });
      }
    }

    // Body: we send plain text (not quill html) - skip for authentication category
    if (category !== 'AUTHENTICATION') {
      const bodyText = quillHtmlToText(body);

      const exampleRow = variableSamples
        .sort((a, b) => a.index - b.index)
        .map((v) => v.sample || '');

      const bodyComp: any = { type: 'BODY', text: bodyText };
      if (exampleRow.length > 0) {
        bodyComp.example = { body_text: [exampleRow] };
      }
      comps.push(bodyComp);
    }

    if (footer.trim()) {
      comps.push({ type: 'FOOTER', text: footer });
    }

    if (buttons.length > 0) {
      const btns = buttons.map((b) => {
        if (b.kind === 'quick_reply') return { type: 'QUICK_REPLY', text: b.text };
        if (b.kind === 'url') {
          const urlBtn: any = { type: 'URL', text: b.text, url: b.url };
          if (b.url_type === 'dynamic' && b.example_url) {
            urlBtn.example = [b.example_url];
          }
          return urlBtn;
        }
        if (b.kind === 'phone') return { type: 'PHONE_NUMBER', text: b.text, phone_number: b.phone };
        if (b.kind === 'otp') return { type: 'OTP', otp_type: b.otp_type };
        if (b.kind === 'flow') {
          const flowBtn: any = {
            type: 'FLOW',
            text: b.text,
            flow_id: b.flow_id || '{{flow-id}}',
            flow_action: b.flow_action || 'navigate'
          };
          if (b.navigate_screen) {
            flowBtn.navigate_screen = b.navigate_screen;
          }
          return flowBtn;
        }
        return undefined;
      }).filter(Boolean);
      if (btns.length) comps.push({ type: 'BUTTONS', buttons: btns });
    }

    return comps;
  }

  function buildPayload() {
    return {
      name,
      language,
      category,
      allow_category_change: true,
      components: buildComponents(),
      wabaId: wabaId, // Include wabaId from session storage
    };
  }

  const isValid = () => {
    const valid = name.trim().length > 0 &&
           language.length > 0 &&
           category.length > 0 &&
           quillHtmlToText(body).trim().length > 0 &&
           !uploadingHeader;
    
    console.log('Validation check:', {
      name: name.trim().length > 0,
      language: language.length > 0,
      category: category.length > 0,
      body: quillHtmlToText(body).trim().length > 0,
      headerType,
      headerMediaId: headerMediaId,
      uploadingHeader,
      isValid: valid
    });
    
    return valid;
  };

  const handleSubmit = () => {
    if (!isValid()) {
      setError('Please fill in all required fields');
      return;
    }
    setOpenConfirm(true);
  };

  const handleCancel = () => {
    setOpenConfirm(false);
  };

  const handleConfirm = () => {
    confirmCreate();
  };

  const confirmCreate = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const payload = buildPayload();
      
      // Debug logging for template creation
      console.log('Creating template with payload:', JSON.stringify(payload, null, 2));
      console.log('Header type:', headerType);
      console.log('Header media ID:', headerMediaId);
      console.log('Header media URL:', headerMediaUrl);
      
      let data;
      if (isEditMode && templateId) {
        // Update existing template
        data = await updateTemplate(templateId, payload);
      } else {
        // Create new template
        data = await createTemplate(wabaId, payload);
      }

      const successMessage = isEditMode ? 'Template updated successfully!' : 'Template submitted for approval.';
      setSuccess(successMessage);
      console.log(isEditMode ? 'UPDATE_TEMPLATE_RESPONSE' : 'CREATE_TEMPLATE_RESPONSE', data);
    
      // Navigate to the list page
      navigate('/events/whatsapp-list-template', { replace: true });
    } catch (e: any) {
      // Prefer backend error message over generic 500
      let raw = e?.response?.data;
      // If backend returned a string, try to parse it
      if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch { /* ignore */ }
      }

      // Support multiple nesting shapes observed from the API
      const errObj =
        raw?.error?.error || // { error: { error: {...} } }
        raw?.error ||
        raw;

      const apiMsg =
        errObj?.error_user_msg ||
        errObj?.message ||
        raw?.message ||
        e?.message;

      // Add helpful hint when name format is wrong
      const blame = errObj?.error_data?.blame_field_specs;
      const nameFormatHint = Array.isArray(blame) && JSON.stringify(blame).includes('name')
        ? ' Template name must be lowercase letters and underscores only (e.g., order_update_v1).'
        : '';

      setError((apiMsg || `Failed to ${isEditMode ? 'update' : 'create'} template`) + nameFormatHint);
    } finally {
      setSubmitting(false);
      setOpenConfirm(false);
    }
  };

  // --- UI

  // Show error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-4">Please log in to access template creation.</p>
          <button
            onClick={() => navigate('/signin')}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Show error if wabaId is not available
  if (!wabaId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Error: WABA ID not found</h2>
          <p className="text-gray-600 mb-4">Please log in again to access template creation.</p>
          <button
            onClick={() => navigate('/events/whatsapp-list-template')}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Go to Template List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">
            {isEditMode ? 'Edit WhatsApp Template' : 'Create WhatsApp Template'}
          </h1>
          <p className="text-sm text-gray-500">
            {isEditMode ? 'Edit your existing template with the form below.' : 'Single‑page builder with variables, buttons, and live preview.'}
          </p>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
        )}
        {success && (
          <div className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{success}</div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left: Form */}
          <div className="space-y-6 rounded-2xl border border-gray-200 p-5 shadow-sm">
            {/* Meta */}
            <div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField label="Template name *" placeholder="e.g., order_update_v1" id="template_name" type="text" value={name} onChange={(e: any) => setName(e.target.value)} />
                <SelectField id="language" label="Language *" placeholder={LANGUAGES[0].label} options={LANGUAGES} onChange={(e) => setLanguage(e.target.value)} />
                <SelectField id="category" label="Category *" placeholder={CATEGORIES[0].label} options={CATEGORIES} onChange={(e) => setCategory(e.target.value)} />
              </div>
            </div>

            {/* Header */}
            <div>
              <h3 className="mb-2 text-base font-semibold">Header (optional)</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <SelectField 
                  id="headerType" 
                  label="Type" 
                  placeholder="Image" 
                  options={HEADER_TYPES} 
                  selectdValue={headerType}
                  onChange={(e) => {
                    setHeaderType(e.target.value as HeaderType);
                    setHeaderUpload(null);
                    setHeaderMediaId(null);
                    setHeaderMediaUrl(null);
                  }}
                />
                
                {headerType === 'text' && (
                  <InputField 
                    id="headerText"
                    label="Header text" 
                    placeholder="Enter header text" 
                    type="text" 
                    value={headerText} 
                    onChange={(e: any) => setHeaderText(e.target.value)} 
                  />
                )}
                
                {(headerType === 'image' || headerType === 'video' || headerType === 'document') && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Upload {headerType}</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={
                        headerType === 'image' ? SUPPORTED_IMAGE_TYPES.join(',') :
                        headerType === 'video' ? SUPPORTED_VIDEO_TYPES.join(',') :
                        SUPPORTED_DOCUMENT_TYPES.join(',')
                      }
                      onChange={handleHeaderMediaChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {uploadingHeader && <p className="text-sm text-blue-600">Uploading...</p>}
                  </div>
                )}
              </div>
              
              {/* Header Preview */}
              {headerType !== 'none' && (
                <div className="mt-4 rounded-lg border border-gray-200 p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Header Preview:</h4>
                  {headerType === 'text' && (
                    <p className="text-sm text-gray-600">{headerText}</p>
                  )}
                  {headerType === 'image' && headerMediaUrl && (
                    <img src={headerMediaUrl} alt="Header preview" className="max-w-xs rounded" />
                  )}
                  {headerType === 'video' && headerMediaUrl && (
                    <video src={headerMediaUrl} controls className="max-w-xs rounded" />
                  )}
                  {headerType === 'document' && headerMediaUrl && (
                    <p className="text-sm text-gray-600">Document: {headerUpload?.name}</p>
                  )}
                  {headerMediaUrl && (
                    <button
                      onClick={removeHeaderMedia}
                      className="mt-2 text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Body */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-base font-semibold">Message body *</h3>
                <button type="button" onClick={() => handleAddVariableAtCursor(quillRef.current)} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">+ Add variable</button>
              </div>
              <p className="mb-2 text-xs text-gray-500">Variables appear as {'{{n}}'} (e.g., {'{{1}}'}).</p>
              
              <ReactQuill
                ref={quillRef}
                value={body}
                onChange={setBody}
                placeholder="Enter your message here..."
                className="min-h-[120px]"
              />
              
              {bodyVariables.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Variable samples:</h4>
                  {variableSamples.map((v) => (
                    <div key={v.index} className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{'{{' + v.index + '}}'}:</span>
                      <input
                        type="text"
                        value={v.sample}
                        onChange={(e) => updateSample(v.index, e.target.value)}
                        placeholder={`Sample for variable ${v.index}`}
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                      <button type="button" className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => removeVariable(v.index)} title="Remove this variable from message">
                        <RiDeleteBinLine />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div>
              <InputField 
                id="footer"
                label="Footer (optional)" 
                placeholder="e.g., To opt out, type STOP" 
                type="text" 
                value={footer} 
                onChange={(e: any) => setFooter(e.target.value)} 
              />
            </div>

            {/* Buttons */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-base font-semibold">Buttons (optional)</h3>
                <div className="flex gap-2">
                  <button type="button" onClick={() => addButton('quick_reply')} className="rounded-lg bg-gray-600 px-3 py-1.5 text-sm text-white hover:bg-gray-700">+ Quick reply</button>
                  <button type="button" onClick={() => addButton('url')} className="rounded-lg bg-gray-600 px-3 py-1.5 text-sm text-white hover:bg-gray-700">+ URL</button>
                  <button type="button" onClick={() => addButton('phone')} className="rounded-lg bg-gray-600 px-3 py-1.5 text-sm text-white hover:bg-gray-700">+ Phone</button>
                  <button type="button" onClick={() => addButton('otp')} className="rounded-lg bg-gray-600 px-3 py-1.5 text-sm text-white hover:bg-gray-700">+ OTP</button>
                  <button type="button" onClick={() => addButton('flow')} className="rounded-lg bg-gray-600 px-3 py-1.5 text-sm text-white hover:bg-gray-700">+ Flow</button>
                </div>
              </div>
              
              {buttons.map((btn, index) => (
                <div key={index} className="mb-3 rounded-lg border border-gray-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{btn.kind.replace('_', ' ').toUpperCase()}</span>
                    <button type="button" onClick={() => removeButton(index)} className="text-red-600 hover:text-red-700">
                      <RiDeleteBinLine />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    <InputField
                      id={`button-text-${index}`}
                      label="Button text"
                      placeholder="Enter button text"
                      type="text"
                      value={btn.text}
                      onChange={(e: any) => updateButton(index, { text: e.target.value })}
                    />
                    
                    {btn.kind === 'url' && (
                      <>
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">URL Type</label>
                          <div className="flex gap-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name={`url-type-${index}`}
                                checked={btn.url_type === 'static'}
                                onChange={() => updateButton(index, { url_type: 'static' })}
                                className="mr-2"
                              />
                              Static
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name={`url-type-${index}`}
                                checked={btn.url_type === 'dynamic'}
                                onChange={() => updateButton(index, { url_type: 'dynamic' })}
                                className="mr-2"
                              />
                              Dynamic
                            </label>
                          </div>
                        </div>
                        <InputField
                          id={`button-url-${index}`}
                          label="URL"
                          placeholder={btn.url_type === 'dynamic' ? "https://example.com/{{1}}" : "https://example.com"}
                          type="text"
                          value={btn.url || ''}
                          onChange={(e: any) => updateButton(index, { url: e.target.value })}
                        />
                        {btn.url_type === 'dynamic' && (
                          <InputField
                            id={`button-example-url-${index}`}
                            label="Example URL"
                            placeholder="https://example.com/value1"
                            type="text"
                            value={btn.example_url || ''}
                            onChange={(e: any) => updateButton(index, { example_url: e.target.value })}
                          />
                        )}
                      </>
                    )}
                    
                    {btn.kind === 'phone' && (
                      <InputField
                        id={`button-phone-${index}`}
                        label="Phone number"
                        placeholder="+1234567890"
                        type="text"
                        value={btn.phone || ''}
                        onChange={(e: any) => updateButton(index, { phone: e.target.value })}
                      />
                    )}
                    
                    {btn.kind === 'otp' && (
                      <SelectField
                        id={`button-otp-${index}`}
                        label="OTP type"
                        placeholder="One Tap"
                        options={[
                          { value: 'ONE_TAP', label: 'One Tap' },
                          { value: 'ZERO_TAP', label: 'Zero Tap' }
                        ]}
                        selectdValue={btn.otp_type || 'ONE_TAP'}
                        onChange={(e: any) => updateButton(index, { otp_type: e.target.value })}
                      />
                    )}
                    
                    {btn.kind === 'flow' && (
                      <>
                        <InputField
                          id={`button-flow-id-${index}`}
                          label="Flow ID"
                          placeholder="{{flow-id}} or actual flow ID"
                          type="text"
                          value={btn.flow_id || ''}
                          onChange={(e: any) => updateButton(index, { flow_id: e.target.value })}
                        />
                        <InputField
                          id={`button-navigate-screen-${index}`}
                          label="Navigate Screen (optional)"
                          placeholder="SCREEN_ID"
                          type="text"
                          value={btn.navigate_screen || ''}
                          onChange={(e: any) => updateButton(index, { navigate_screen: e.target.value })}
                        />
                        <SelectField
                          id={`button-flow-action-${index}`}
                          label="Flow Action"
                          placeholder="navigate"
                          options={[
                            { value: 'navigate', label: 'Navigate' }
                          ]}
                          selectdValue={btn.flow_action || 'navigate'}
                          onChange={(e: any) => updateButton(index, { flow_action: e.target.value })}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Submit */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/events/whatsapp-list-template')}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isValid() || submitting || uploadingHeader}
                className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : isEditMode ? 'Update Template' : 'Submit Template'}
              </button>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="space-y-6 rounded-2xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-lg font-semibold">Preview</h3>
            
            <div className="space-y-4">
              {/* Header Preview */}
              {headerType !== 'none' && (
                <div className="rounded-lg bg-gray-50 p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Header:</h4>
                  {headerType === 'text' && (
                    <p className="text-sm text-gray-600">{headerText}</p>
                  )}
                  {headerType === 'image' && headerPreviewUrl && (
                    <img src={headerPreviewUrl} alt="Header preview" className="max-w-full rounded" />
                  )}
                  {headerType === 'video' && headerPreviewUrl && (
                    <video src={headerPreviewUrl} controls className="max-w-full rounded" />
                  )}
                  {headerType === 'document' && headerPreviewUrl && (
                    <p className="text-sm text-gray-600">Document: {headerUpload?.name}</p>
                  )}
                </div>
              )}

              {/* Body Preview */}
              <div className="rounded-lg bg-gray-50 p-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Body:</h4>
                <div className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: compiledPreview }} />
              </div>

              {/* Footer Preview */}
              {footer && (
                <div className="rounded-lg bg-gray-50 p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Footer:</h4>
                  <p className="text-sm text-gray-600">{footer}</p>
                </div>
              )}

              {/* Buttons Preview */}
              {buttons.length > 0 && (
                <div className="rounded-lg bg-gray-50 p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Buttons:</h4>
                  <div className="space-y-1">
                    {buttons.map((btn, index) => (
                      <div key={index} className="text-sm text-gray-600">
                        {btn.text} 
                        {btn.kind === 'url' && ` (${btn.url})`}
                        {btn.kind === 'phone' && ` (${btn.phone})`}
                        {btn.kind === 'flow' && ` [Flow ID: ${btn.flow_id || '{{flow-id}}'}${btn.navigate_screen ? `, Screen: ${btn.navigate_screen}` : ''}]`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {openConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">
                {isEditMode ? 'Update Template' : 'Submit Template'}
              </h3>
              <p className="text-gray-600 mb-6">
                {isEditMode ? 'Are you sure you want to update this template?' : 'Are you sure you want to submit this template for approval?'}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : isEditMode ? 'Update' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}